import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Building, CheckCircle, Mail, Search, User, Users, X } from 'lucide-react';
import { BRAND_KEY } from '../../config/brand';
import { useCourtierLight } from '../../hooks/useCourtierLight';

const isDna = BRAND_KEY === 'dna';

const MailContactPickerModal = ({
  activeContactTab,
  composeForm,
  contactPickerField,
  contactResults,
  contactSearchQuery,
  currentPage,
  deselectAllFromTab,
  getSelectedCountByCategory,
  getUniqueCompanies,
  isEmailSelected,
  openCompanyContacts,
  openCourtierClients,
  paginateItems,
  renderPagination,
  searchContacts,
  selectAllFromTab,
  selectContactEmail,
  setActiveContactTab,
  setContactSearchQuery,
  setShowContactPicker
}) => {
  const { t } = useTranslation('admin');
  const isCourtierLight = useCourtierLight();
  return (
    <>
        <div data-testid="mail-contact-picker-modal" className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1300] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900 flex items-center">
                  <BookOpen size={24} className="mr-3 text-indigo-600" />
                  {t('mail.pickRecipientTitle', { field: contactPickerField.toUpperCase() })}
                </h3>
                {composeForm[contactPickerField] && (() => {
                  const counts = getSelectedCountByCategory();
                  return (
                    <div className="mt-3 flex items-center space-x-4">
                      <div className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 rounded-lg border border-indigo-200">
                        <span className="text-sm font-medium text-indigo-900">{t('mail.totalLabel')}</span>
                        <span className="text-lg font-bold text-indigo-600">{counts.total}</span>
                      </div>
                      
                      {counts.clients > 0 && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                          <User size={14} className="text-blue-600" />
                          <span className="text-sm text-blue-700">{t('mail.nbClients', { count: counts.clients })}</span>
                        </div>
                      )}
                      
                      {counts.courtiers > 0 && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 rounded-lg">
                          <User size={14} className="text-green-600" />
                          <span className="text-sm text-green-700">{t('mail.nbCourtiers', { count: counts.courtiers })}</span>
                        </div>
                      )}
                      
                      {counts.companies > 0 && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-50 rounded-lg">
                          <Building size={14} className="text-purple-600" />
                          <span className="text-sm text-purple-700">{t('mail.nbCompanies', { count: counts.companies })}</span>
                        </div>
                      )}
                      {isDna && counts.assisteurs > 0 && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 rounded-lg">
                          <Users size={14} className="text-teal-600" />
                          <span className="text-sm text-teal-700">{t('mail.nbAssisteurs', { count: counts.assisteurs })}</span>
                        </div>
                      )}
                      {isDna && counts.assistance_clients > 0 && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-cyan-50 rounded-lg">
                          <User size={14} className="text-cyan-600" />
                          <span className="text-sm text-cyan-700">{t('mail.nbAssistanceClients', { count: counts.assistance_clients })}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <button
                onClick={() => setShowContactPicker(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b border-slate-200">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder={t('mail.pickerSearchPlaceholder')}
                  value={contactSearchQuery}
                  onChange={(e) => {
                    setContactSearchQuery(e.target.value);
                    searchContacts(e.target.value);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="px-4 border-b border-slate-200">
              <div className="flex overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveContactTab('clients')}
                  className={`px-4 py-3 font-medium transition-colors relative whitespace-nowrap shrink-0 ${
                    activeContactTab === 'clients'
                      ? 'text-indigo-600 border-b-2 border-indigo-600'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>{t('mail.tabClients')}</span>
                  <span className="ml-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
                    {contactResults.clients.length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveContactTab('courtiers')}
                className={`px-4 py-3 font-medium transition-colors relative whitespace-nowrap shrink-0 ${
                  activeContactTab === 'courtiers'
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-slate-600 hover:text-slate-900'
                } ${isCourtierLight ? 'hidden' : ''}`}
              >
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>{t('mail.tabCourtiers')}</span>
                  <span className="ml-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
                    {contactResults.courtiers.length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveContactTab('companies')}
                className={`px-4 py-3 font-medium transition-colors relative whitespace-nowrap shrink-0 ${
                  activeContactTab === 'companies'
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Building size={16} />
                  <span>{t('mail.tabCompanies')}</span>
                  <span className="ml-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
                    {contactResults.companies.length}
                  </span>
                </div>
              </button>
              {isDna && (
              <button
                onClick={() => setActiveContactTab('assisteurs')}
                className={`px-4 py-3 font-medium transition-colors relative whitespace-nowrap shrink-0 ${
                  activeContactTab === 'assisteurs'
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Users size={16} />
                  <span>{t('mail.tabAssisteurs')}</span>
                  <span className="ml-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
                    {(contactResults.assisteurs || []).length}
                  </span>
                </div>
              </button>
              )}
              {isDna && (
              <button
                onClick={() => setActiveContactTab('assistance_clients')}
                className={`px-4 py-3 font-medium transition-colors relative whitespace-nowrap shrink-0 ${
                  activeContactTab === 'assistance_clients'
                    ? 'text-cyan-600 border-b-2 border-cyan-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>{t('mail.tabAssistanceClients')}</span>
                  <span className="ml-1 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
                    {(contactResults.assistance_clients || []).length}
                  </span>
                </div>
              </button>
              )}
              </div>{/* fin div scrollable */}
            </div>{/* fin div outer avec border-b */}


            {/* Action Buttons */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                {activeContactTab === 'clients' && t('mail.clientsFound', { count: contactResults.clients.length })}
                {activeContactTab === 'courtiers' && t('mail.courtiersFound', { count: contactResults.courtiers.length })}
                {activeContactTab === 'companies' && t('mail.companiesFound', { count: contactResults.companies.length })}
                {isDna && activeContactTab === 'assisteurs' && t('mail.assisteursFound', { count: (contactResults.assisteurs || []).length })}
                {isDna && activeContactTab === 'assistance_clients' && t('mail.assistanceClientsFound', { count: (contactResults.assistance_clients || []).length })}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={selectAllFromTab}
                  className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center space-x-2"
                >
                  <CheckCircle size={14} />
                  <span>{t('mail.selectAll')}</span>
                </button>
                <button
                  onClick={deselectAllFromTab}
                  className="px-4 py-1.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors text-sm font-medium"
                >
                  {t('mail.deselectAll')}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeContactTab === 'clients' && (
                <div className="space-y-2">
                  {contactResults.clients.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">{t('mail.noClientFound')}</p>
                  ) : (
                    paginateItems(contactResults.clients, currentPage.clients).map((contact) => (
                      <div key={contact.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-slate-900 mb-2">{contact.name}</p>
                            <div className="space-y-1">
                              {contact.emails.map((email, index) => (
                                <button
                                  key={index}
                                  onClick={() => selectContactEmail(email)}
                                  className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                                    isEmailSelected(email)
                                      ? 'bg-green-100 text-green-800 border border-green-300'
                                      : 'bg-white text-indigo-600 hover:bg-indigo-50 border border-slate-200'
                                  }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Mail size={14} />
                                    <span className="text-sm">{email}</span>
                                  </div>
                                  {isEmailSelected(email) && (
                                    <CheckCircle size={16} className="text-green-600" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                            {t('mail.linkedClient')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeContactTab === 'courtiers' && (
                <div className="space-y-2">
                  {contactResults.courtiers.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">{t('mail.noCourtierFound')}</p>
                  ) : (
                    paginateItems(contactResults.courtiers, currentPage.courtiers).map((contact) => (
                      <div key={contact.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-slate-900">{contact.name}</p>
                              <button
                                onClick={() => openCourtierClients(contact)}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-xs font-medium flex items-center space-x-1"
                              >
                                <User size={12} />
                                <span>{t('mail.viewClients')}</span>
                              </button>
                            </div>
                            <div className="space-y-1">
                              {contact.emails.map((email, index) => (
                                <button
                                  key={index}
                                  onClick={() => selectContactEmail(email)}
                                  className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                                    isEmailSelected(email)
                                      ? 'bg-green-100 text-green-800 border border-green-300'
                                      : 'bg-white text-indigo-600 hover:bg-indigo-50 border border-slate-200'
                                  }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Mail size={14} />
                                    <span className="text-sm">{email}</span>
                                  </div>
                                  {isEmailSelected(email) && (
                                    <CheckCircle size={16} className="text-green-600" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
                            {t('mail.linkedCourtier')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeContactTab === 'companies' && (
                <div className="space-y-2">
                  {contactResults.companies.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">{t('mail.noCompanyFound')}</p>
                  ) : (
                    paginateItems(getUniqueCompanies(), currentPage.companies).map((company, index) => (
                      <div key={index} className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <Building size={20} className="text-purple-600" />
                              <div>
                                <p className="font-bold text-slate-900">{company.name}</p>
                                <p className="text-sm text-slate-600">
                                  {t('mail.contactsAndEmails', { contacts: company.contactCount, emails: company.totalEmails })}
                                </p>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => openCompanyContacts(company)}
                            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium flex items-center space-x-2"
                          >
                            <Mail size={14} />
                            <span>{t('mail.viewContacts')}</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {isDna && activeContactTab === 'assisteurs' && (
                <div className="space-y-2">
                  {(contactResults.assisteurs || []).length === 0 ? (
                    <p className="text-center text-slate-500 py-8">{t('mail.noAssisteurFound')}</p>
                  ) : (
                    paginateItems(contactResults.assisteurs || [], currentPage.assisteurs).map((contact) => (
                      <div key={contact.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-slate-900 mb-2">{contact.name}</p>
                            <div className="space-y-1">
                              {contact.emails.map((email, index) => (
                                <button
                                  key={index}
                                  onClick={() => selectContactEmail(email)}
                                  className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                                    isEmailSelected(email)
                                      ? 'bg-green-100 text-green-800 border border-green-300'
                                      : 'bg-white text-teal-600 hover:bg-teal-50 border border-slate-200'
                                  }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Mail size={14} />
                                    <span className="text-sm">{email}</span>
                                  </div>
                                  {isEmailSelected(email) && (
                                    <CheckCircle size={16} className="text-green-600" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium ml-3 shrink-0">
                            {t('mail.badgeAssisteur')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {isDna && activeContactTab === 'assistance_clients' && (
                <div className="space-y-2">
                  {(contactResults.assistance_clients || []).length === 0 ? (
                    <p className="text-center text-slate-500 py-8">{t('mail.noAssistanceClientFound')}</p>
                  ) : (
                    paginateItems(contactResults.assistance_clients || [], currentPage.assistance_clients).map((contact) => (
                      <div key={contact.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-slate-900 mb-2">{contact.name}</p>
                            <div className="space-y-1">
                              {contact.emails.map((email, index) => (
                                <button
                                  key={index}
                                  onClick={() => selectContactEmail(email)}
                                  className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                                    isEmailSelected(email)
                                      ? 'bg-green-100 text-green-800 border border-green-300'
                                      : 'bg-white text-cyan-600 hover:bg-cyan-50 border border-slate-200'
                                  }`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <Mail size={14} />
                                    <span className="text-sm">{email}</span>
                                  </div>
                                  {isEmailSelected(email) && (
                                    <CheckCircle size={16} className="text-green-600" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded-lg text-xs font-medium ml-3 shrink-0">
                            {t('mail.badgeAssistanceClient')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Pagination */}
            {activeContactTab === 'clients' && renderPagination('clients', contactResults.clients.length)}
            {activeContactTab === 'courtiers' && renderPagination('courtiers', contactResults.courtiers.length)}
            {activeContactTab === 'companies' && renderPagination('companies', getUniqueCompanies().length)}
            {isDna && activeContactTab === 'assisteurs' && renderPagination('assisteurs', (contactResults.assisteurs || []).length)}
            {isDna && activeContactTab === 'assistance_clients' && renderPagination('assistance_clients', (contactResults.assistance_clients || []).length)}

            {/* Footer */}
            <div className="p-4 border-t border-slate-200">
              <button
                onClick={() => setShowContactPicker(false)}
                className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
              >
                {t('mail.close')}
              </button>
            </div>
          </div>
        </div>
    </>
  );
};

export default MailContactPickerModal;
